import './AuthScreen.css';
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
} from 'lucide-react';
import { useLang } from '../i18n';

export default function AuthScreen({ setMiState }) {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRegister, setIsRegister] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    if (typeof setMiState === 'function') setMiState('thinking');
    try {
      await signInWithPopup(auth, googleProvider);
      if (typeof setMiState === 'function') setMiState('success');
    } catch (err) {
      console.error('[AUTH-GOOGLE] Error:', err.code, err.message);
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(t('auth_error_generic'));
        if (typeof setMiState === 'function') setMiState('warning');
      } else if (typeof setMiState === 'function') {
        setMiState('neutral');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !password) {
      setError(t('auth_error_fill_email_password'));
      return;
    }
    setError('');
    setLoading(true);
    if (typeof setMiState === 'function') setMiState('thinking');

    try {
      if (isRegister) {
        const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        await sendEmailVerification(userCredential.user);
      } else {
        await signInWithEmailAndPassword(auth, cleanEmail, password);
      }
      if (typeof setMiState === 'function') setMiState('success');
    } catch (err) {
      console.error('[AUTH-EMAIL] Error:', err.code, err.message);
      const errCode = err.code || '';
      if (isRegister && errCode === 'auth/email-already-in-use') {
        setError(t('auth_error_email_in_use'));
      } else if (errCode === 'auth/wrong-password' || errCode === 'auth/invalid-credential') {
        setError(t('auth_error_check_credentials'));
      } else if (errCode === 'auth/weak-password') {
        setError(t('auth_error_weak_password'));
      } else if (errCode === 'auth/invalid-email') {
        setError(t('auth_error_invalid_email'));
      } else {
        setError(t('auth_error_generic'));
      }
      if (typeof setMiState === 'function') setMiState('warning');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError(t('auth_error_reset_email_required'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setError(t('auth_error_invalid_email'));
      return;
    }

    setError('');
    setLoading(true);
    if (typeof setMiState === 'function') setMiState('thinking');

    try {
      await sendPasswordResetEmail(auth, cleanEmail);
      setResetSent(true);
      if (typeof setMiState === 'function') setMiState('success');
    } catch (err) {
      console.error('[AUTH-RESET] Error:', err.code, err.message);
      if (err.code === 'auth/user-not-found') {
        setError(t('auth_error_user_not_found'));
      } else if (err.code === 'auth/invalid-email') {
        setError(t('auth_error_invalid_email'));
      } else {
        setError(t('auth_error_reset_failed'));
      }
      if (typeof setMiState === 'function') setMiState('warning');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (registerMode) => {
    setIsRegister(registerMode);
    setIsForgotPassword(false);
    setResetSent(false);
    setError('');
  };

  return (
    <main className="gph-auth">
      <div className="gph-auth__frame">

        <motion.section
          className="gph-auth__panel"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          aria-label={t('auth_panel_aria')}
        >
          {isForgotPassword ? (
            <div className="gph-auth-card">
              <div className="gph-auth-card__header">
                <span>{t('auth_reset_eyebrow')}</span>
                <h2>{t('auth_reset_title')}</h2>
                <p>{t('auth_reset_desc')}</p>
              </div>

              {!resetSent ? (
                <>
                  <label className="gph-auth-field">
                    <span>{t('auth_email')}</span>
                    <div>
                      <Mail size={18} />
                      <input
                        type="email"
                        placeholder="you@email.com"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        onKeyDown={(event) => event.key === 'Enter' && handleForgotPassword()}
                        autoComplete="email"
                      />
                    </div>
                  </label>

                  {error && <div className="gph-auth-error">{error}</div>}

                  <button
                    type="button"
                    className="gph-auth-primary gph-btn-primary"
                    onClick={handleForgotPassword}
                    disabled={loading}
                  >
                    {loading ? <span className="gph-auth-spinner" /> : null}
                    {loading ? t('auth_sending') : t('auth_send_link')}
                  </button>
                </>
              ) : (
                <div className="gph-auth-success">
                  <CheckCircle2 size={28} />
                  <strong>{t('auth_email_sent_title')}</strong>
                  <p>{t('auth_email_sent_desc')}</p>
                </div>
              )}

              <button
                type="button"
                className="gph-auth-link"
                onClick={() => {
                  setIsForgotPassword(false);
                  setResetSent(false);
                  setError('');
                }}
              >
                {t('auth_back_to_login')}
              </button>
            </div>
          ) : (
            <div className="gph-auth-card">
              <div className="gph-auth-card__header">
                <h2>{isRegister ? t('auth_create_free_account') : t('auth_email_login')}</h2>
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="gph-auth-google gph-btn-white"
              >
                {loading ? (
                  <span className="gph-auth-spinner" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                {loading ? t('auth_entering') : t('auth_google')}
              </button>

              <div className="gph-auth-divider">
                <span />
                <p>{t('auth_or_email')}</p>
                <span />
              </div>

              <label className="gph-auth-field">
                <span>{t('auth_email')}</span>
                <div>
                  <Mail size={18} />
                  <input
                    type="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                  />
                </div>
              </label>

              <label className="gph-auth-field">
                <span>{t('auth_password')}</span>
                <div>
                  <Lock size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth_password_placeholder')}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && handleEmailAuth()}
                    autoComplete={isRegister ? 'new-password' : 'current-password'}
                  />
                  <button
                    type="button"
                    className="gph-auth-eye"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('auth_hide_password') : t('auth_show_password')}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              {!isRegister && (
                <button
                  type="button"
                  className="gph-auth-forgot"
                  onClick={() => {
                    setIsForgotPassword(true);
                    setError('');
                  }}
                >
                  {t('auth_forgot_password')}
                </button>
              )}

              {error && <div className="gph-auth-error">{error}</div>}

              <button
                type="button"
                className="gph-auth-primary gph-btn-primary"
                onClick={handleEmailAuth}
                disabled={loading}
              >
                {loading ? <span className="gph-auth-spinner" /> : null}
                {loading ? t('auth_entering') : isRegister ? t('auth_create_free_account') : t('auth_email_login')}
              </button>

              <div className="gph-auth-switch" role="group" aria-label={t('auth_switch_aria')}>
                <button
                  type="button"
                  className={isRegister ? 'is-active' : ''}
                  onClick={() => switchMode(true)}
                >
                  {t('auth_create_account_short')}
                </button>
                <button
                  type="button"
                  className={!isRegister ? 'is-active' : ''}
                  onClick={() => switchMode(false)}
                >
                  {t('auth_login_short')}
                </button>
              </div>

              <p className="gph-auth-terms">{t('auth_terms_microcopy')}</p>
            </div>
          )}

        </motion.section>
      </div>
    </main>
  );
}

export function getDeviceFingerprint() {
  return navigator.userAgent || 'unknown-device';
}
